import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { NotifyService } from '../../ui/core/notify.service';
import { SKIP_FEEDBACK } from '../../shared/http/feedback.context';

/**
 * Global error-feedback interceptor.
 *
 * On any failed request it surfaces a toast, then RETHROWS the error so
 * components handling the error inline still receive it (the toast is
 * additive, never swallowing).
 *
 * Two cases are intentionally NOT toasted:
 *  - Requests that opt out via the SKIP_FEEDBACK HttpContext token — the
 *    component owns the feedback and would otherwise get a double toast.
 *  - 401 responses — the auth interceptor already refreshes/retries those,
 *    and a spurious toast would flash during the transparent refresh.
 */
export const errorFeedbackInterceptor: HttpInterceptorFn = (req, next) => {
  // i18n/asset loads must NOT run this interceptor: it injects TranslateService,
  // which itself loads translations via HttpClient. Injecting it here during
  // that first load is a circular DI that leaves translations unloaded (raw
  // keys). Let those requests pass straight through untouched.
  if (req.url.includes('i18n/')) {
    return next(req);
  }
  // Resolve feedback services LAZILY (below, at error time) via the Injector —
  // never eagerly at the top. Eagerly injecting NotifyService/TranslateService
  // here builds a construction-time cycle (interceptor → TranslateService →
  // HttpClient) that corrupts the injector, surfacing as unloaded translations
  // AND — under the ngx-toastr this replaced — "reading 'config' of undefined"
  // crashes on the first toast. NotifyService resolves TranslateService the
  // same lazy way internally, for the same reason.
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // CSRF failures are rethrown silently by the auth interceptor; never toast them.
      const isCsrf = `${err?.error?.message ?? ''} ${err?.error?.error ?? ''}`
        .toLowerCase()
        .includes('csrf');

      if (req.context.get(SKIP_FEEDBACK) || err.status === 401 || isCsrf) {
        return throwError(() => err);
      }

      const notify = injector.get(NotifyService);
      const translate = injector.get(TranslateService);

      // NestJS class-validator returns `message` as a string[]; normalize it.
      const raw = err?.error?.message;
      const message = Array.isArray(raw)
        ? raw.filter((m) => typeof m === 'string').join(' · ')
        : typeof raw === 'string' && raw
          ? raw
          : translate.instant('COMMON.TOAST.GENERIC_ERROR');
      notify.error(message);

      return throwError(() => err);
    }),
  );
};
